import Logger from './logger';
import { DataClassification } from './dataEncryptionService';

// Data masking configuration
interface MaskingConfig {
  emailMask: string;
  phoneMask: string;
  ssnMask: string;
  creditCardMask: string;
  nameMask: string;
  addressMask: string;
}

// Masking result
interface MaskingResult {
  originalValue: string;
  maskedValue: string;
  maskType: string;
  classification: DataClassification;
  maskedAt: number;
}

// PII detection patterns
interface PIIDetectionPattern {
  name: string;
  pattern: RegExp;
  classification: DataClassification;
  maskFunction: (value: string) => string;
}

class DataMaskingService {
  private config: MaskingConfig;
  private piiPatterns: PIIDetectionPattern[] = [];

  constructor() {
    this.config = {
      emailMask: '***@***.***',
      phoneMask: '(***) ***-****',
      ssnMask: '***-**-****',
      creditCardMask: '****-****-****-****',
      nameMask: '*** ***',
      addressMask: '*** *** St, ***, ** *****'
    };

    this.initializePIIPatterns();
  }

  private initializePIIPatterns(): void {
    this.piiPatterns = [
      {
        name: 'email',
        pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        classification: DataClassification.RESTRICTED,
        maskFunction: this.maskEmail.bind(this)
      },
      {
        name: 'phone',
        pattern: /^[\+]?[1-9][\d]{0,15}$/,
        classification: DataClassification.RESTRICTED,
        maskFunction: this.maskPhone.bind(this)
      },
      {
        name: 'ssn',
        pattern: /^\d{3}-?\d{2}-?\d{4}$/,
        classification: DataClassification.RESTRICTED,
        maskFunction: this.maskSSN.bind(this)
      },
      {
        name: 'creditCard',
        pattern: /^\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}$/,
        classification: DataClassification.RESTRICTED,
        maskFunction: this.maskCreditCard.bind(this)
      },
      {
        name: 'name',
        pattern: /^[A-Za-z\s]{2,50}$/,
        classification: DataClassification.CONFIDENTIAL,
        maskFunction: this.maskName.bind(this)
      },
      {
        name: 'address',
        pattern: /^\d+\s+[A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd|Drive|Dr|Lane|Ln|Boulevard|Blvd|Way|Circle|Cir|Court|Ct|Place|Pl)/i,
        classification: DataClassification.CONFIDENTIAL,
        maskFunction: this.maskAddress.bind(this)
      }
    ];
  }

  // Individual masking functions
  private maskEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    const maskedLocal = localPart.length > 2 
      ? localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1]
      : '*'.repeat(localPart.length);
    const [domainName, tld] = domain.split('.');
    const maskedDomain = domainName.length > 2
      ? domainName[0] + '*'.repeat(domainName.length - 2) + domainName[domainName.length - 1]
      : '*'.repeat(domainName.length);
    return `${maskedLocal}@${maskedDomain}.${tld}`;
  }

  private maskPhone(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(***) ***-${cleaned.slice(-4)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (***) ***-${cleaned.slice(-4)}`;
    }
    return this.config.phoneMask;
  }

  private maskSSN(ssn: string): string {
    const cleaned = ssn.replace(/\D/g, '');
    if (cleaned.length === 9) {
      return `***-**-${cleaned.slice(-4)}`;
    }
    return this.config.ssnMask;
  }

  private maskCreditCard(card: string): string {
    const cleaned = card.replace(/\D/g, '');
    if (cleaned.length >= 13 && cleaned.length <= 19) {
      return `****-****-****-${cleaned.slice(-4)}`;
    }
    return this.config.creditCardMask;
  }

  private maskName(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].length > 2 
        ? parts[0][0] + '*'.repeat(parts[0].length - 2) + parts[0][parts[0].length - 1]
        : '*'.repeat(parts[0].length);
    }
    return parts.map(part => 
      part.length > 2 
        ? part[0] + '*'.repeat(part.length - 2) + part[part.length - 1]
        : '*'.repeat(part.length)
    ).join(' ');
  }

  private maskAddress(address: string): string {
    const parts = address.split(/\s+/);
    if (parts.length >= 3) {
      // Mask street number and name, keep city and state
      const streetNumber = parts[0];
      const streetName = parts.slice(1, -2).join(' ');
      const cityState = parts.slice(-2).join(' ');
      
      const maskedStreetNumber = '*'.repeat(streetNumber.length);
      const maskedStreetName = streetName.split(' ').map(word => 
        word.length > 2 
          ? word[0] + '*'.repeat(word.length - 2) + word[word.length - 1]
          : '*'.repeat(word.length)
      ).join(' ');
      
      return `${maskedStreetNumber} ${maskedStreetName} ${cityState}`;
    }
    return this.config.addressMask;
  }

  // Public API methods
  detectPII(value: string): PIIDetectionPattern | null {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    
    for (const pattern of this.piiPatterns) {
      if (pattern.pattern.test(trimmedValue)) {
        return pattern;
      }
    }

    return null;
  }

  maskValue(value: string, classification?: DataClassification): MaskingResult {
    if (!value || typeof value !== 'string') {
      return {
        originalValue: value,
        maskedValue: value,
        maskType: 'none',
        classification: DataClassification.PUBLIC,
        maskedAt: Date.now()
      };
    }

    const trimmedValue = value.trim();
    const detectedPII = this.detectPII(trimmedValue);
    
    let maskedValue: string;
    let maskType: string;
    let finalClassification: DataClassification;

    if (detectedPII) {
      maskedValue = detectedPII.maskFunction(trimmedValue);
      maskType = detectedPII.name;
      finalClassification = detectedPII.classification;
    } else if (classification) {
      // Apply generic masking based on classification
      switch (classification) {
        case DataClassification.RESTRICTED:
          maskedValue = '*'.repeat(Math.min(trimmedValue.length, 8));
          maskType = 'restricted';
          break;
        case DataClassification.CONFIDENTIAL:
          maskedValue = trimmedValue.length > 4 
            ? trimmedValue[0] + '*'.repeat(trimmedValue.length - 2) + trimmedValue[trimmedValue.length - 1]
            : '*'.repeat(trimmedValue.length);
          maskType = 'confidential';
          break;
        case DataClassification.INTERNAL:
          maskedValue = trimmedValue.length > 6
            ? trimmedValue.slice(0, 2) + '*'.repeat(trimmedValue.length - 4) + trimmedValue.slice(-2)
            : '*'.repeat(trimmedValue.length);
          maskType = 'internal';
          break;
        default:
          maskedValue = trimmedValue;
          maskType = 'none';
      }
      finalClassification = classification;
    } else {
      maskedValue = trimmedValue;
      maskType = 'none';
      finalClassification = DataClassification.PUBLIC;
    }

    return {
      originalValue: trimmedValue,
      maskedValue,
      maskType,
      classification: finalClassification,
      maskedAt: Date.now()
    };
  }

  maskDocument(document: any, fieldsToMask?: string[]): any {
    const maskedDoc = { ...document };
    const maskingMetadata: any[] = [];

    const fieldsToProcess = fieldsToMask || Object.keys(document);

    for (const fieldName of fieldsToProcess) {
      if (document[fieldName] && typeof document[fieldName] === 'string') {
        try {
          const maskingResult = this.maskValue(document[fieldName]);
          if (maskingResult.maskType !== 'none') {
            maskedDoc[fieldName] = maskingResult.maskedValue;
            maskingMetadata.push({
              fieldName,
              maskType: maskingResult.maskType,
              classification: maskingResult.classification,
              maskedAt: maskingResult.maskedAt
            });
          }
        } catch (error) {
          Logger.error(`Failed to mask field ${fieldName}`, error instanceof Error ? error : new Error('Unknown error'));
        }
      }
    }

    // Add masking metadata
    if (maskingMetadata.length > 0) {
      maskedDoc._maskingMetadata = maskingMetadata;
    }

    return maskedDoc;
  }

  unmaskValue(maskingResult: MaskingResult): string {
    // Note: This is a simplified unmasking - in production, you'd need secure storage
    // of the original values or proper key management
    Logger.warn('Unmasking attempted - this should only be done with proper authorization');
    return maskingResult.originalValue;
  }

  getMaskingStats(document: any): {
    totalFields: number;
    maskedFields: number;
    classifications: Record<string, number>;
  } {
    const metadata = document._maskingMetadata || [];
    const classifications: Record<string, number> = {};

    metadata.forEach((item: any) => {
      classifications[item.classification] = (classifications[item.classification] || 0) + 1;
    });

    return {
      totalFields: Object.keys(document).length,
      maskedFields: metadata.length,
      classifications
    };
  }

  // Utility method to check if a value is already masked
  isMasked(value: string): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }

    // Check for common masking patterns
    const maskingPatterns = [
      /\*+/,  // Contains asterisks
      /^\*+$/,  // Only asterisks
      /^.{1,2}\*+.{1,2}$/,  // Partially masked
      /^.{1}\*+.{1}$/  // Email/name style masking
    ];

    return maskingPatterns.some(pattern => pattern.test(value));
  }

  // Get configuration
  getConfig(): MaskingConfig {
    return { ...this.config };
  }

  // Update configuration
  updateConfig(newConfig: Partial<MaskingConfig>): void {
    this.config = { ...this.config, ...newConfig };
    Logger.info('Data masking configuration updated');
  }
}

// Export singleton instance
const dataMaskingService = new DataMaskingService();
export default dataMaskingService;
export { type MaskingResult, type PIIDetectionPattern };
